import "../../assets/styles/header.css";
import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoBell } from "react-icons/go";
import { TfiMenuAlt } from "react-icons/tfi";
import { UserRound, MessageSquareText, ClipboardList } from "lucide-react";
import { useAuth } from "../../features/auth/AuthProvider";
import { useEnquiryContext } from "../../context/EnquiryContext";
import FallbackImage from "../common/fallbackImage";
import { useViewEnquiryList } from "../../hooks/enquiries/viewEnquiryList";

/* ─────────────────────────────────────────────────────────────
   BELL DROPDOWN — isolated so a failing API never kills Header
───────────────────────────────────────────────────────────── */
function EnquiryBell({ scopeKey }) {
  const navigate = useNavigate();
  const location = useLocation();
  const bellRef = useRef(null);
  const [bellOpen, setBellOpen] = useState(false);
  const { readEnquiries, markMultipleAsRead, markAsRead } = useEnquiryContext();

  const { data: enquiriesResponse, isError } = useViewEnquiryList({
    page: 1,
    limit: 20,
    scopeKey,
  });

  const pendingEnquiries = useMemo(() => {
    if (isError) return [];
    if (!Array.isArray(enquiriesResponse?.enquiries)) return [];
    return enquiriesResponse.enquiries.filter(
      (e) => String(e.status || "").toLowerCase() === "pending",
    );
  }, [enquiriesResponse?.enquiries, isError]);

  const unreadCount = useMemo(() => {
    return pendingEnquiries.filter((e) => !readEnquiries.has(e._id || e.id))
      .length;
  }, [pendingEnquiries, readEnquiries]);

  // Mark all pending enquiries as read when on /enquiries page
  useEffect(() => {
    if (location.pathname === "/enquiries" && pendingEnquiries.length > 0) {
      const enquiryIds = pendingEnquiries.map((e) => e._id || e.id);
      markMultipleAsRead(enquiryIds);
    }
  }, [location.pathname, pendingEnquiries, markMultipleAsRead]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setBellOpen(false);
      }
    };
    if (bellOpen) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [bellOpen]);

  return (
    <>
      {/* ── ENQUIRIES BUTTON ── */}
      <button
        className="header-enquiries-btn"
        onClick={() => {
          const enquiryIds = pendingEnquiries.map((e) => e._id || e.id);
          markMultipleAsRead(enquiryIds);
          navigate("/enquiries");
        }}
        aria-label="Go to Enquiries"
      >
        <ClipboardList size={16} />
        <span>Enquiries</span>
        {/* {unreadCount > 0 && (
          <span className="header-enquiries-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )} */}
      </button>

      {/* ── BELL ── */}
      <div className="notif-bell-wrapper" ref={bellRef}>
        <button
          className="notif-bell-btn"
          onClick={() => setBellOpen((prev) => !prev)}
          aria-label="Enquiry notifications"
        >
          <GoBell size={22} className="notificationIcon" />
        </button>

        {/* {bellOpen && (
          <div className="notif-dropdown">
            <div className="notif-dropdown-header">
              <span>Enquiries</span>
              {unreadCount > 0 && (
                <span className="notif-count-label">{unreadCount} pending</span>
              )}
            </div>

            <ul className="notif-list">
              {pendingEnquiries.length === 0 ? (
                <li className="notif-empty">No pending enquiries</li>
              ) : (
                pendingEnquiries.slice(0, 8).map((enq) => {
                  const id = enq._id || enq.id;
                  return (
                    <li
                      key={id}
                      className="notif-item"
                      onClick={() => {
                        markAsRead(id);
                        navigate(`/enquiries/view/${id}`);
                        setBellOpen(false);
                      }}
                    >
                      <span className="notif-item-icon">
                        <MessageSquareText size={16} />
                      </span>
                      <div className="notif-item-body">
                        <p className="notif-item-name">
                          {enq.name || "Unknown"}
                        </p>
                        <p className="notif-item-subject">
                          {enq.subject || enq.message || "No subject"}
                        </p>
                      </div>
                      <span className="notif-item-dot" />
                    </li>
                  );
                })
              )}
            </ul>

            <button
              className="notif-view-all"
              onClick={() => {
                const enquiryIds = pendingEnquiries.map((e) => e._id || e.id);
                markMultipleAsRead(enquiryIds);
                navigate("/enquiries");
                setBellOpen(false);
              }}
            >
              View all enquiries
            </button>
          </div>
        )} */}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN HEADER
───────────────────────────────────────────────────────────── */
export default function Header({ onToggleSidebar }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const { user } = useAuth();

  const showSearchOnPages = [
    "/products",
    "/dashboard",
    "/industries",
    "/tags/",
    "/distributors",
  ];
  const showSearch = showSearchOnPages.includes(location.pathname);

  const displayName = user?.name || "Company Admin";
  const displayEmail = user?.email || "";
  const isOnProfile = location.pathname === "/profile";

  return (
    <header>
      <nav className="header_nav">
        <div className="header_container">
          {/* HAMBURGER */}
          <div className="menuSearch">
            <TfiMenuAlt
              onClick={onToggleSidebar}
              className="menubar"
              size={25}
            />
          </div>

          {/* SEARCH (conditional) */}
          {/* <div className="header_title" ref={dropdownRef}>
            {showSearch && (
              <div className="header_search">
                <i className="fa-solid fa-magnifying-glass search-icon" />
                <input type="text" placeholder="Search..." className="w-full" />
              </div>
            )}
          </div> */}

          {/* ENQUIRIES BUTTON — left side */}
          <EnquiryBell scopeKey={user?.id || user?.companyId} />

          {/* RIGHT SIDE */}
          <div className="profile">
            <div className="profile-divider" />

            {/* USER INFO — clickable → /profile */}
            <div
              className={`profile-info-row profile-info-clickable${isOnProfile ? " profile-info-active" : ""}`}
              role="button"
              tabIndex={0}
              title="View your profile"
              onClick={() => navigate("/profile")}
              onKeyDown={(e) => e.key === "Enter" && navigate("/profile")}
            >
              {user?.logo ? (
                <FallbackImage
                  src={user.logo}
                  alt={displayName}
                  className="profile-avatar-img"
                />
              ) : (
                <UserRound size={35} className="profile-avatar" />
              )}
              <div className="profile-text">
                <p className="dropdown-name">{displayName}</p>
                {displayEmail && (
                  <p className="dropdown-email">{displayEmail}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
